import React, { Component } from 'react';
import { Card, Alert } from 'react-bootstrap';

import * as d3 from 'd3'
import * as dagreD3 from 'dagre-d3'

import RevisionDetailsView from 'components/controls/RevisionDetailsView/index';

import './index.css';

/**
 * flowkit/flowEdge.js's <marker>, as a dagre-d3 arrowhead.
 *
 * dagre-d3's own is 8x6 in `strokeWidth` units, so it grows and shrinks with the line; this one is
 * 11.2x5.6 in user space, which is what the reference draws at every stroke width. Registered
 * through `renderer.arrows()` — the library's own extension point — rather than by reaching into
 * the rendered SVG afterwards.
 *
 * NO PAINT HERE. The marker's fill arrives from `.edgePath path` in index.css, which reaches it
 * because dagre-d3 puts each edge's <defs> INSIDE its own `g.edgePath`. A `fill=` attribute would
 * also drop the var() on ios_saf 11 and render the arrowhead black — see src/svgPaint.guard.test.js.
 */
function bhArrowhead(parent, id) {
  parent.append("marker")
    .attr("id", id)
    .attr("viewBox", "0 0 16 8")
    .attr("refX", 16)
    .attr("refY", 4)
    .attr("markerUnits", "userSpaceOnUse")
    .attr("markerWidth", 11.2)
    .attr("markerHeight", 5.6)
    .attr("orient", "auto")
    .append("path")
    .attr("d", "M0,0 L16,4 L0,8 Z");
}

/**
 * The clicked node's plate, as the workflow editor paints a selected one.
 *
 * The plate IS a `.bh-flow-node`, so selection is that component's own `.bh-flow-node-selected` —
 * the darker plate, the deeper lift and the dark accent bar — rather than a second palette. It is
 * applied on the click because dagre-d3 rebuilds every label from `nodeLabel()` on a re-render.
 *
 * `group` is the clicked `g.node`, or null for a node that selects nothing: a node with no
 * selection sends the details pane back to the graph, and the canvas has to agree with it.
 *
 * A module-level function so tools/css/dnnParity.py can inline it and drive the real one.
 */
function applyNodeSelection(root, group) {
  var plates = root.querySelectorAll("g.node .bh-flow-node");
  for ( var i = 0; i < plates.length; i++ ) {
    plates[i].classList.remove("bh-flow-node-selected");
  }
  var plate = group ? group.querySelector(".bh-flow-node") : null;
  if ( plate ) {
    plate.classList.add("bh-flow-node-selected");
  }
}

export { applyNodeSelection };

/**
 * The plate is the label div, so the <rect> is only a hit target and an edge anchor: square, and
 * sized to the plate EXACTLY rather than to the plate plus dagre's own 10px all round — padding
 * dagre would otherwise add outside the plate, leaving every edge stopping 10px short of the node
 * it points at.
 *
 * A module-level function rather than an inline loop so tools/css/dnnParity.py can inline it and
 * drive the real one; a probe measuring its own copy of the geometry measures nothing.
 */
function applyNodeGeometry(g) {
  g.nodes().forEach(function(v) {
    var node = g.node(v);
    node.rx = node.ry = 0;
    node.paddingX = node.paddingY = 0;
  });
}

export default class GraphPreview extends Component {
  constructor(props) {
    super(props);
    this.svg = null;
    this.inner = null;
    this.zoom = null;

    // This flag is used to indicate that rendering is needed but is deferred for some reason.
    // We use a number of entry points to check for it and trigger rendering.
    this.needsRender = false;
  }

  componentDidUpdate(prevProps) {
    // See if graph needs to be rendered (possibly all stars are aligned now)
    if ( this.needsRender ) {
      this.renderGraph();
    }

    // Render if new graph object is loaded 
    if (this.props.shell.graph !== prevProps.shell.graph) {
      this.renderGraph();
    }
  }

  /**
   * A node's label, as flowkit's own markup.
   *
   * dagre-d3 renders an html label into a <foreignObject> as a real HTML <div>, and a real HTML box
   * can simply BE a `.bh-flow-node` — plate, 4px accent bar, lift, padding and type scale included,
   * with nothing copied out of flowkit.css. The <rect> behind it paints nothing; `index.css` turns
   * it off and `paddingX/Y: 0` makes it end where the plate does, so an edge meets the plate.
   * See docs/specs/2026-10-14-graph-onnx-node-parity.md.
   */
  nodeLabel(title, subtitles) {
    return '<div class="bh-flow-node"><div class="bh-flow-node-main">'
      + '<div class="bh-flow-node-title">' + title + '</div>'
      + subtitles.map(line => '<div class="bh-flow-node-subtitle">' + line + '</div>').join('')
      + '</div></div>';
  }

  formatShape(shape) {
    var shape_str = "";
    var separator = "[";
    for ( var dim of shape.dim ) {
      var dim_value = ( "" != dim.dim_param ? dim.dim_param : dim.dim_value );
      shape_str += separator + dim_value;
      separator = ",";
    }
    shape_str += "]"
    return shape_str;
  }

  renderGraph() {
    // See if there is anything to render
    if ( !this.props.shell.graph ) {
      return;
    }

    // Check to see if the viewport is ready. Defer rendering otherwise
    var graphElement = document.getElementById("graph");
    if ( !graphElement || 0 == graphElement.clientWidth || 0 == graphElement.clientHeight ) {
      this.needsRender = true;
      return;
    }

    // Initialize graph
    var g = new dagreD3.graphlib.Graph({compound:true}).setGraph({});
    if ( this.svg ) {
      this.svg.call(this.zoom.transform, d3.zoomIdentity);
    }

    //
    // Construct graph
    //

    var allNodeInputIndex = {};
    var allNodeOutputIndex = {};
    var initIndex = {};
    var valuesIndex = {};
    var graphOutputIndex = {};
    for (var init of this.props.shell.graph.graph.initializer) {
      initIndex[init.name] = init;
      valuesIndex[init.name] = init;
    }
    for ( var input of this.props.shell.graph.graph.input ) {
      valuesIndex[input.name] = input;
    };
    for ( var output of this.props.shell.graph.graph.output ) {
      graphOutputIndex[output.name] = output;
      valuesIndex[output.name] = output;
    };
    for ( var value of this.props.shell.graph.graph.value_info ) {
      valuesIndex[value.name] = value;
    };

    for (var node of this.props.shell.graph.graph.node) {
      var subtitles = [];
      for (var input of node.input) {
        if ( initIndex[input] ) {
          var val = valuesIndex[input];
          subtitles.push(input + " " + ( val ? this.formatShape(val.type.tensor_type.shape) : "" ));
        }
      }
      var label = this.nodeLabel(node.op_type, subtitles);

      g.setNode(
        node.name,
        { label: label, labelType: "html" });
      var uiVertex = g.node(node.name);
      uiVertex.node = node;
      uiVertex.selection = {
        type: "node",
        node: node
      };

      for (var input of node.input) {
        allNodeInputIndex[input] = uiVertex;
      }
      for (var output of node.output) {
        allNodeOutputIndex[output] = uiVertex;
      }
    }

    for (var node of this.props.shell.graph.graph.node) {
      for (var input of node.input) {
        if ( allNodeOutputIndex[input] ) {
          var value = valuesIndex[input];
          var shape = value ? this.formatShape(value.type.tensor_type.shape) : "";
          g.setEdge(
            allNodeOutputIndex[input].node.name,
            node.name,
            { curve: d3.curveBasis, label: shape, arrowhead: "bhArrow" });
        } else {
          if ( !initIndex[input] ) {
            // Add global input
            var label = this.nodeLabel("Input", [input]);
            g.setNode(
              input,
              { label: label, labelType: "html" });
            var uiVertex = g.node(input);
            uiVertex.selection = null;

            var value = valuesIndex[input];
            var shape = value ? this.formatShape(value.type.tensor_type.shape) : "";
            g.setEdge(
              input,
              node.name,
              { curve: d3.curveBasis, label: shape, arrowhead: "bhArrow" });
          }
        }
      }

      for (var output of node.output) {
        if ( graphOutputIndex[output] ) {
          // Add global output
          var label = this.nodeLabel("Output", [output]);
          g.setNode(
            output,
            { label: label, labelType: "html" });
          var uiVertex = g.node(output);
          uiVertex.selection = null;

          var value = valuesIndex[output];
          var shape = value ? this.formatShape(value.type.tensor_type.shape) : "";
          g.setEdge(
            node.name,
            output,
            { curve: d3.curveBasis, label: shape, arrowhead: "bhArrow" });
        }
      }
    }

    //
    // Configure and render graph
    //

    applyNodeGeometry(g);

    // Find/create DOM nodes for the graph
    if ( !this.svg ) {
      this.svg = d3.select("#graph");
      this.inner = this.svg.append("g");

      // Set up zoom support
      var that = this;
      this.zoom = d3.zoom().on("zoom", function() {
        that.inner.attr("transform", d3.event.transform);
      });
      this.svg.call(this.zoom);
    }
    var svg = this.svg;
    var inner = this.inner;
    var zoom = this.zoom;

    // Run the renderer. This is what draws the final graph.
    var renderer = new dagreD3.render();
    renderer.arrows(Object.assign({}, renderer.arrows(), { bhArrow: bhArrowhead }));
    renderer(inner, g);

    // Setup click handler
    svg.selectAll("g.node").on("click", name => {
      var uiVertex = g.node(name);
      // d3 v5 publishes the element the listener is on for the duration of the dispatch; this stays
      // an arrow function so `this` is still the component.
      applyNodeSelection(svg.node(), uiVertex.selection ? d3.event.currentTarget : null);
      if ( uiVertex.selection ) {
        this.props.shell.shellInterface.updateSelection(uiVertex.selection.type, uiVertex.selection.node);
      } else {
        this.props.shell.shellInterface.updateSelection("graph", null);
      }
    });

    // Center the graph
    if ( g.graph().height < 10000 ) {
      var padding = 20;
      var graphElement = document.getElementById("graph");
      var initialScale = graphElement.clientHeight / ( g.graph().height + padding );
      svg.call(zoom.transform, d3.zoomIdentity.translate(
        graphElement.clientWidth / 2 - ( ( g.graph().width + padding ) * initialScale ) / 2,
        0).scale(initialScale));
    } else {
      var topNode = g.node(g.nodes()[0]);
      g.nodes().forEach(function(v) {
        var node = g.node(v);
        if ( node.y < topNode.y ) {
          topNode = node;
        }
      });

      var graphElement = document.getElementById("graph");
      var initialScale = 0.2;
      svg.call(zoom.transform, d3.zoomIdentity.translate(
        graphElement.clientWidth / 2 - topNode.x * initialScale,
        0).scale(initialScale));
    }

    //
    this.needsRender = false;
  }

  render() {
    // See if graph needs to be rendered (possibly all stars are aligned now)
    if ( this.needsRender ) {
      this.renderGraph();
    }

    // Render the widget itself
    return (
      <>
        <Card>
          <Card.Header>
            {this.props.revision
              ? <RevisionDetailsView
                  app={this.props.app}
                  revision={this.props.revision}
                  shell={this.props.shell}
                />
              : <Alert variant="primary" className="mb-0">Loading revision info...</Alert>
            }
          </Card.Header>
          <Card.Body>
            <div className="graph-container">
              <svg id="graph" width="100%" height="100%"></svg>
            </div>
          </Card.Body>
        </Card>
      </>
    )
  }
}
